import { supabase } from '@/lib/supabase';
import { mapWithConcurrency } from '@/lib/concurrency';
import type {
  QuestionRegion,
  QuestionIdentifierResult,
  ExtractedQuestion,
} from '@/types/pipeline';
import { extractQuestionsFromRegions } from '@/types/pipeline';

type ModelConfig = {
  provider: string;
  model: string;
};

/**
 * Process multiple pages through the question identifier API and merge results.
 * This is the canonical implementation used by NewAssignmentPage.tsx
 * 
 * @param pages - Array of image data URLs (one per page)
 * @param modelConfig - Optional override; omit to use the server-side admin setting
 * @param onProgress - Optional callback for progress updates
 * @returns Merged question identifier result and extracted questions
 */
export async function processQuestionIdentifier(
  pages: string[],
  /** Omit to use the admin panel's 題目偵測 setting (the normal path). */
  modelConfig: ModelConfig | undefined,
  onProgress?: (current: number, total: number, message: string) => void
): Promise<{
  questionResult: QuestionIdentifierResult;
  extractedQuestions: ExtractedQuestion[];
}> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;

  const headers = {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  };

  // Detect pages concurrently (bounded) — detection latency is per-page VLM
  // latency, so N pages sequentially cost N× while parallel costs ~1×.
  let completedPages = 0;
  onProgress?.(0, pages.length, `正在偵測 ${pages.length} 頁…`);
  const perPageRegions = await mapWithConcurrency(pages, 3, async (page, pageIdx) => {
    const response = await fetch('/api/dev/model-eval/question-identifier', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        image: page,
        // modelConfig omitted on purpose: the server uses the admin panel's
        // 題目偵測 setting, so model choice is governed in one place.
        ...(modelConfig ? { modelConfig } : {}),
      }),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }

    const data = await response.json();
    completedPages++;
    onProgress?.(completedPages, pages.length, `已偵測 ${completedPages} / ${pages.length} 頁…`);
    return (data.regions || []).map((r: QuestionRegion) => ({
      ...r,
      pageIndex: pageIdx,
    })) as QuestionRegion[];
  });
  // Flatten in page order so global renumbering below stays deterministic
  const rawRegions: QuestionRegion[] = perPageRegions.flat();

  // Renumber regions globally across pages so sections and questions
  // are numbered sequentially (S1, S2, S3... / S1-Q1, S1-Q2, S2-Q1...)
  const allRegions = (() => {
    if (pages.length <= 1) return rawRegions;

    let globalSectionNum = 0;
    let questionInSection = 0;
    let currentSectionId = '';
    const idMap = new Map<string, string>();

    return rawRegions.map((r) => {
      const oldId: string = r.questionId || '';
      let newId = oldId;

      if (r.type === 'section-instruction') {
        globalSectionNum++;
        questionInSection = 0;
        const newSectionBase = `S${globalSectionNum}`;
        newId = `${newSectionBase}-ins`;
        currentSectionId = newId;
        idMap.set(oldId, newId);
        idMap.set(oldId.replace('-ins', ''), newSectionBase);
      } else if (r.type === 'section-content') {
        newId = `S${globalSectionNum}-content`;
        idMap.set(oldId, newId);
      } else if (r.type === 'question' || r.type === 'mc-question') {
        questionInSection++;
        newId = `S${globalSectionNum}-Q${questionInSection}`;
        idMap.set(oldId, newId);
      } else if (r.type?.startsWith('answer-')) {
        const parentOld = r.parentQuestion || '';
        const parentNew = idMap.get(parentOld) || parentOld;
        newId = `${parentNew}-ans`;
        idMap.set(oldId, newId);
        return {
          ...r,
          questionId: newId,
          parentQuestion: parentNew,
          parentSection: idMap.get(r.parentSection || '') || r.parentSection,
        };
      }

      return {
        ...r,
        questionId: newId,
        parentSection: idMap.get(r.parentSection || '') || r.parentSection,
      };
    });
  })();

  const mergedResult: QuestionIdentifierResult = {
    regions: allRegions,
    pageCount: pages.length,
    confidence: 0.9,
  };

  const extracted = extractQuestionsFromRegions(allRegions);

  return {
    questionResult: mergedResult,
    extractedQuestions: extracted,
  };
}
